import NodeData, {
  NodeDataType,
  type NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import type {
  IConfigurationContentData,
  IConfigurationPoEInterface,
} from 'src/types/configuration';
import { isV4Format, calculateNetworkAddress2 } from 'src/utils/ip';

import { RESERVED_AUTO_DEPLOY_VLAN } from './import-topology-shared';
import type { ImportedPeerCandidate } from './import-topology-types';
import { buildBridgeByVidMap } from './import-topology-vlan-bridge';
import { mapDeviceType } from './map-device-type';

const getPoeByInterfaceIndex = (
  category: NodeCategoryModel,
  poeInterfaces: IConfigurationPoEInterface[] = [],
) => {
  const poePortIndexes = (category.ports || [])
    .filter((port) => port.isPoE)
    .map((port) => ({
      portId: port.id,
      interfaceIndex: category.portIndexes[port.id],
    }))
    .sort((a, b) => a.interfaceIndex - b.interfaceIndex);

  const entries: Array<[number, IConfigurationPoEInterface]> = poePortIndexes
    .map((port, index) => [port.interfaceIndex, poeInterfaces[index]] as const)
    .filter(
      (entry): entry is [number, IConfigurationPoEInterface] => !!entry[1],
    );

  return new Map(entries);
};

const cloneCategoryForPeer = (
  matchName: string,
  existingPeer?: ImportedPeerCandidate,
) => {
  if (existingPeer?.category) {
    return structuredClone(existingPeer.category);
  }
  const nodeType = mapDeviceType(matchName);
  return structuredClone(
    NodeData[nodeType] || NodeData[NodeDataType.GenericDevice],
  );
};

export const getInferredPeersByPort = ({
  config,
  category,
  deviceName,
}: {
  config: IConfigurationContentData;
  category: NodeCategoryModel;
  deviceName: string;
}) => {
  const peers = new Map<number, ImportedPeerCandidate>();
  const bridgeByVid = buildBridgeByVidMap(config);
  const vlanIdsByInterface = new Map<number, number[]>();
  (config.vlan?.vids || []).forEach((vid) => {
    if (vid.vid === RESERVED_AUTO_DEPLOY_VLAN) return;
    vid.members.forEach((member) => {
      const current = vlanIdsByInterface.get(member.interface) || [];
      vlanIdsByInterface.set(member.interface, [...current, vid.vid]);
    });
  });
  const poeInterfaces = config.poe?.interfaces || [];
  const poeByInterfaceIndex = getPoeByInterfaceIndex(category, poeInterfaces);
  const remainingPoeInterfaces: IConfigurationPoEInterface[] = [
    ...poeInterfaces,
  ];

  (config.dhcp?.lease || []).forEach((lease) => {
    const [vlanId = 0, rawPortIndex = -1] = lease.value || [];
    const interfaceIndex = rawPortIndex + 1;
    if (interfaceIndex <= 0 || !isV4Format(lease.ip)) return;

    const existingPeer = peers.get(interfaceIndex);
    const bridge = bridgeByVid.get(vlanId) || bridgeByVid.get(0);
    let poe = poeByInterfaceIndex.get(interfaceIndex);
    if (poe) {
      const poeIndex = remainingPoeInterfaces.indexOf(poe);
      if (poeIndex >= 0) {
        remainingPoeInterfaces.splice(poeIndex, 1);
      }
    }
    if (!poe && remainingPoeInterfaces.length > 0) {
      [poe] = remainingPoeInterfaces.splice(0, 1);
    }
    const ifaceName = config.interface?.interfaces
      ?.find((item) => item.interface === interfaceIndex)
      ?.name?.trim();
    const matchName =
      ifaceName ||
      existingPeer?.matchName ||
      poe?.name?.trim() ||
      `${deviceName} Port ${interfaceIndex}`;
    const categoryData = cloneCategoryForPeer(matchName, existingPeer);

    categoryData.name = matchName;
    if (bridge?.sm) {
      if (vlanId > 0) {
        const currentInterfaces = categoryData.ipInterfaces || [];
        categoryData.ipInterfaces = [
          ...currentInterfaces.filter((item) => item.vlanId !== vlanId),
          {
            vlanId,
            ip: lease.ip,
            subnet: calculateNetworkAddress2(lease.ip, bridge.sm),
            subnetMask: bridge.sm,
            dhcp: true,
          },
        ];
      } else {
        categoryData.ip = lease.ip;
        categoryData.subnetMask = bridge.sm;
        categoryData.subnet = calculateNetworkAddress2(lease.ip, bridge.sm);
        categoryData.dhcp = true;
      }
    }
    if (poe) {
      categoryData.poe = {
        enable: poe.enable,
        fault: poe.fault,
        limit: poe.limit,
        limit_pwr: poe.limit_pwr,
        prio: poe.prio,
      };
    }

    peers.set(interfaceIndex, {
      matchName,
      nodeType: existingPeer?.nodeType ?? mapDeviceType(matchName),
      category: categoryData,
    });
  });

  (config.interface?.interfaces || []).forEach((iface) => {
    if (!iface.up) return;

    const existingPeer = peers.get(iface.interface);
    let poe = poeByInterfaceIndex.get(iface.interface);
    if (poe) {
      const poeIndex = remainingPoeInterfaces.indexOf(poe);
      if (poeIndex >= 0) {
        remainingPoeInterfaces.splice(poeIndex, 1);
      }
    }
    if (!poe && remainingPoeInterfaces.length > 0) {
      [poe] = remainingPoeInterfaces.splice(0, 1);
    }

    const matchName =
      iface.name?.trim() ||
      existingPeer?.matchName ||
      poe?.name?.trim() ||
      `${deviceName} Port ${iface.interface}`;
    if (!matchName) return;

    const categoryData = cloneCategoryForPeer(matchName, existingPeer);
    categoryData.name = matchName;

    if (poe) {
      categoryData.poe = {
        enable: poe.enable,
        fault: poe.fault,
        limit: poe.limit,
        limit_pwr: poe.limit_pwr,
        prio: poe.prio,
      };
    }

    peers.set(iface.interface, {
      matchName,
      nodeType: existingPeer?.nodeType ?? mapDeviceType(matchName),
      category: categoryData,
    });
  });

  Array.from(vlanIdsByInterface.entries()).forEach(
    ([interfaceIndex, vlanIds]) => {
      const existingPeer = peers.get(interfaceIndex);
      if (existingPeer) return;

      const [firstVlanId] = vlanIds;
      const bridge = bridgeByVid.get(firstVlanId);
      const inferredName = `${deviceName} Port ${interfaceIndex}`;
      const categoryData = structuredClone(
        NodeData[mapDeviceType(inferredName)] ||
          NodeData[NodeDataType.GenericDevice],
      );
      categoryData.name = inferredName;
      if (bridge?.ip && bridge?.sm) {
        categoryData.ipInterfaces = vlanIds.map((vlanId) => ({
          vlanId,
          ip: '',
          subnet: calculateNetworkAddress2(bridge.ip, bridge.sm),
          subnetMask: bridge.sm,
        }));
      }

      peers.set(interfaceIndex, {
        matchName: categoryData.name,
        nodeType: mapDeviceType(inferredName),
        category: categoryData,
      });
    },
  );

  return peers;
};
